import pkg, { ExperimentsConfig, setPackageState } from "./pkg";

export const HOLDOUT = "holdout" as const;

export const EXPERIMENT_ASSIGNMENTS_LOCAL_STORAGE_NAME =
  "experimentAssignments";
export const LOGGED_IN_USER_EXP_COOKIE_NAME = "loggedInUserExperimentUid";
export const LOGGED_OUT_USER_EXP_COOKIE_NAME = "loggedOutUserExperimentUid";

export {
  sendExperimentTriggerThroughKafkaRestProxy,
  EVENT_SOURCE,
} from "./experimentTriggers";
export function init(config: ExperimentsConfig) {
  if (pkg.initialized) {
    console.warn("@outschool/experiments-shared has already been initialized");
  } else {
    setPackageState(config);
  }
}

export enum RandomizationUnits {
  /** The activity_uid from the activities table */
  Activity = "Activity",
  /** The slug_id from the activities table */
  ActivitySlug = "ActivitySlug",
  /**
   * Applies to both logged in and logged out users
   * uses the experiment_uid stored in browser storage on the user's device
   */
  AllUsersAnonymousId = "AllUsersAnonymousId",
  /** The learner_uid from the learners table */
  Learner = "Learner",
  /**
   * The user_uid from the users table if the user is logged in,
   * else the experiment_uid stored in browser storage on the user's device
   */
  LoggedInOrLoggedOutUser = "LoggedInOrLoggedOutUser",
  /** The user_uid from the users table */
  LoggedInUser = "LoggedInUser",
  /** The experiment_uid stored in browser storage on the user's device */
  LoggedOutUser = "LoggedOutUser",
}

export type AssignmentRequest = Array<{
  randomizationUnit: RandomizationUnits;
  randomizationUnitIds: string[];
}>;

type AssignmentResponse = Array<{
  randomizationUnit: RandomizationUnits;
  randomizationUnitId: string;
  experimentName: string;
  variantAssigned: string;
}>;

type FetchFunction = (req: RequestInfo, opt: RequestInit) => Promise<Response>;
type ExperimentAssignments = Record<string, string>;

let DEBUG_LOG_EXPERIMENTS_SERVICE_RESPONSE = false;

if (typeof process !== "undefined") {
  DEBUG_LOG_EXPERIMENTS_SERVICE_RESPONSE =
    process?.env?.DEBUG_LOG_EXPERIMENTS_SERVICE_RESPONSE === "enabled";
}

/**
 * Throws error in the event that the timeout exceeds given timeoutMs.
 * @param fetchPromise Promise for which we are timing against
 * @param timeoutMs Timeout time in ms
 * @returns Promise with Response.
 */
function fetchWithTimeout(
  fetchPromise: Promise<Response>,
  debugDescriptor: string,
  timeoutMs: number = 500
) {
  let timeoutRef: NodeJS.Timeout;

  return Promise.race<Response>([
    fetchPromise.finally(() => {
      clearTimeout(timeoutRef);
    }),
    new Promise((_resolve, reject) => {
      timeoutRef = setTimeout(() => {
        reject(
          new Error(
            `Experiments service timed out in ${timeoutMs}ms: ${debugDescriptor}`
          )
        );

        if (DEBUG_LOG_EXPERIMENTS_SERVICE_RESPONSE) {
          fetchPromise
            .then(async response => {
              console.log(
                `timed out fetchPromise eventually resolved with ${response?.status}`,
                {
                  status: response?.status,
                  body: await response?.json?.(),
                  debugDescriptor,
                }
              );
            })
            .catch(err => {
              console.log("timed out fetchPromise eventually rejected with", {
                err,
                debugDescriptor,
              });
            });
        }
      }, timeoutMs);
    }),
  ]);
}

export async function makeGetRequest({
  fetchFn,
  randomizationUnit,
  randomizationUnitId,
  experimentsServiceUrl,
  experimentsApiKey,
  debugDescriptor = "makeGetRequest",
  timeoutMs = undefined,
}: {
  fetchFn: FetchFunction;
  randomizationUnit: RandomizationUnits;
  randomizationUnitId: string;
  experimentsServiceUrl: string;
  experimentsApiKey: string;
  debugDescriptor?: string;
  timeoutMs?: number;
}): Promise<ExperimentAssignments> {
  // guard against undefined values that should not be there that will ultimately return a 400
  // loggedInUserExpUid needs to always be defined, loggedInUserExpUid can be undefined in the case
  // of a user that has never logged in

  if (!randomizationUnitId) {
    return {};
  }

  const resp = await fetchWithTimeout(
    fetchFn(
      `${experimentsServiceUrl}/assignment/${randomizationUnit}/${randomizationUnitId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${experimentsApiKey}`,
        },
      }
    ),
    debugDescriptor,
    timeoutMs
  );

  const assignmentResponse: AssignmentResponse = await resp.json();
  return assignmentResponse.reduce((obj: ExperimentAssignments, assignment) => {
    obj[assignment.experimentName] = assignment.variantAssigned;
    return obj;
  }, {} as ExperimentAssignments);
}

export async function fetchWebsiteExpAssignment({
  fetchFn,
  loggedInUserExpUid,
  loggedOutUserExpUid,
  experimentsServiceUrl,
  experimentsApiKey,
  timeoutMs = undefined,
}: {
  fetchFn: FetchFunction;
  loggedInUserExpUid?: string;
  loggedOutUserExpUid?: string;
  experimentsServiceUrl: string;
  experimentsApiKey: string;
  timeoutMs?: number;
}): Promise<ExperimentAssignments> {
  // guard against undefined values that should not be there that will ultimately return a 400
  // loggedInUserExpUid needs to always be defined, loggedInUserExpUid can be undefined in the case
  // of a user that has never logged in

  if (!loggedOutUserExpUid) {
    return {};
  }

  const resp = await fetchWithTimeout(
    fetchFn(
      `${experimentsServiceUrl}/assignment/website/${loggedOutUserExpUid}/${loggedInUserExpUid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${experimentsApiKey}`,
        },
      }
    ),
    "fetchWebsiteExpAssignment",
    timeoutMs
  );

  const assignmentResponse: AssignmentResponse = await resp.json();
  return assignmentResponse.reduce((obj: ExperimentAssignments, assignment) => {
    obj[assignment.experimentName] = assignment.variantAssigned;
    return obj;
  }, {} as ExperimentAssignments);
}

export function fetchLearnerExpAssignments({
  fetchFn,
  learnerUid,
  experimentsServiceUrl,
  experimentsApiKey,
  timeoutMs = undefined,
}: {
  fetchFn: FetchFunction;
  learnerUid: string;
  experimentsServiceUrl: string;
  experimentsApiKey: string;
  timeoutMs?: number;
}): Promise<ExperimentAssignments> {
  return makeGetRequest({
    fetchFn,
    randomizationUnit: RandomizationUnits.Learner,
    randomizationUnitId: learnerUid,
    experimentsServiceUrl,
    experimentsApiKey,
    debugDescriptor: "fetchLearnerExpAssignments",
    timeoutMs,
  });
}
